import { z } from "zod";
import { EQUIPMENT_ARCHIVE_REASONS, EQUIPMENT_CATEGORIES } from "./types.js";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");
const optionalText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .max(max, message)
    .transform((v) => (v === "" ? null : v))
    .nullish();

export const listEquipmentInput = z.object({
  confirmationId: z.string().optional(),
  /** Avec les machines sorties du parc — l'écran du bureau seulement. */
  includeArchived: z.boolean().optional(),
});
export type ListEquipmentInput = z.infer<typeof listEquipmentInput>;

const equipmentFields = {
  name: z.string().trim().min(1, "Donnez-lui un nom").max(120, "Nom trop long"),
  typeLabel: z.string().trim().min(1, "Choisissez ou saisissez un type").max(80, "Type trop long"),
  category: z.enum(EQUIPMENT_CATEGORIES),
  brand: optionalText(80, "Marque trop longue"),
  model: optionalText(80, "Modèle trop long"),
  serialNumber: optionalText(80, "Numéro de série trop long"),
  ownership: z.enum(["owned", "rented"]),
  rentalCompany: optionalText(80, "Nom du loueur trop long"),
  rentalReturnOn: isoDate.nullish(),
  notes: optionalText(1000, "Notes trop longues"),
};

export const addEquipmentInput = z.object({
  confirmationId: z.string().optional(),
  ...equipmentFields,
  /** Livrée directement sur un chantier, comme une location. Le dépôt sinon. */
  projectId: z.string().uuid().nullish(),
});
export type AddEquipmentInput = z.infer<typeof addEquipmentInput>;

export const updateEquipmentInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  ...equipmentFields,
});
export type UpdateEquipmentInput = z.infer<typeof updateEquipmentInput>;

export const archiveEquipmentInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  reason: z.enum(EQUIPMENT_ARCHIVE_REASONS),
});
export type ArchiveEquipmentInput = z.infer<typeof archiveEquipmentInput>;

export const moveEquipmentInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  toKind: z.enum(["depot", "chantier", "reparation"]),
  toProjectId: z.string().uuid().nullish(),
  note: optionalText(500, "Note trop longue"),
});
export type MoveEquipmentInput = z.infer<typeof moveEquipmentInput>;

const bulkFields = {
  name: z.string().trim().min(1, "Donnez-lui un nom").max(120, "Nom trop long"),
  typeLabel: z.string().trim().min(1, "Choisissez ou saisissez un type").max(80, "Type trop long"),
  category: z.enum(EQUIPMENT_CATEGORIES),
  unit: z.string().trim().min(1).max(20),
  ownership: z.enum(["owned", "rented"]),
  rentalCompany: optionalText(80, "Nom du loueur trop long"),
  rentalReturnOn: isoDate.nullish(),
  totalQuantity: z.number().int("Un nombre entier").min(0).max(100_000),
  notes: optionalText(1000, "Notes trop longues"),
};

export const addBulkItemInput = z.object({ confirmationId: z.string().optional(), ...bulkFields });
export type AddBulkItemInput = z.infer<typeof addBulkItemInput>;

export const updateBulkItemInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  ...bulkFields,
});
export type UpdateBulkItemInput = z.infer<typeof updateBulkItemInput>;

export const archiveBulkItemInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  reason: z.enum(EQUIPMENT_ARCHIVE_REASONS),
});
export type ArchiveBulkItemInput = z.infer<typeof archiveBulkItemInput>;

export const moveBulkItemInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  fromKind: z.enum(["depot", "chantier", "reparation"]),
  /** Le chantier, quand c'en est un ; nul sinon. */
  fromProjectId: z.string().uuid().nullable(),
  toKind: z.enum(["depot", "chantier", "reparation"]),
  toProjectId: z.string().uuid().nullable(),
  quantity: z.number().int("Un nombre entier").min(1, "Dites combien"),
  note: optionalText(500, "Note trop longue"),
});
export type MoveBulkItemInput = z.infer<typeof moveBulkItemInput>;

export const setEquipmentPhotoInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid(),
  /** Le fichier déjà déposé dans `equipment-photos` ; nul pour retirer la photo. */
  photoPath: z.string().min(1).max(300).nullable(),
});
export type SetEquipmentPhotoInput = z.infer<typeof setEquipmentPhotoInput>;

export const equipmentHistoryInput = z.object({
  confirmationId: z.string().optional(),
  equipmentId: z.string().uuid().optional(),
  bulkItemId: z.string().uuid().optional(),
});
export type EquipmentHistoryInput = z.infer<typeof equipmentHistoryInput>;
